<script setup>
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'

definePage({
  meta: { ...PageMeta.Templates.Preview.Meta, pageTitle: PageMeta.Templates.Preview.Title },
})
import TemplatePreviewPage from '@/views/setup/emails/templates/preview/TemplatePreviewPage.vue'
import { useTemplatesStore } from '@/@vxapp/Messages/Templates/useTemplatesStore'

import router from '@/router'

const log = createLogger('[guid]')
const route = useRoute()
const templateStore = useTemplatesStore()

// Loading states
const isLoading = ref(false)
const isInitializing = ref(true)
const hasError = ref(false)

const fetchTemplate = async guid => {
  try {
    isLoading.value = true
    hasError.value = false
    await templateStore.fetch(guid)
  } catch (error) {
    log.error('Failed to fetch template:', error)
    hasError.value = true
  } finally {
    isLoading.value = false
    isInitializing.value = false
  }
}

watch(
  () => route.params.guid,
  async newVal => {
    if (newVal) {
      await fetchTemplate(newVal)
    }
  },
  { deep: true, immediate: true }
)

/* ** */
</script>

<template>
  <VCard>
    <!-- Loading State -->
    <div v-if="isInitializing || isLoading">
      <VCard :loading="true">
        <VOverlay
          :model-value="isInitializing || isLoading"
          contained
          persistent
          scroll-strategy="none"
          class="align-center justify-center"
        >
          <VProgressCircular indeterminate />
        </VOverlay>
        <VCardText class="text-center">
          <p>Loading template preview...</p>
        </VCardText>
      </VCard>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError">
      <VAlert
        type="error"
        variant="tonal"
        class="mb-4"
      >
        <VAlertTitle>Error Loading Template</VAlertTitle>
        <p>Failed to load the template preview. Please try again.</p>
        <template #append>
          <VBtn
            variant="tonal"
            color="error"
            @click="fetchTemplate(route.params.guid)"
            >Retry</VBtn
          >
        </template>
      </VAlert>
    </div>

    <!-- Content State -->
    <VCardItem v-else-if="templateStore.template">
      <!-- Horizontal Menu / Navigation -->
      <VRow>
        <VCol cols="6">
          <VCardTitle class="text-h4">
            {{ templateStore.template?.templateName }}
          </VCardTitle>
          <VCardSubtitle>Template ID #{{ templateStore.template?.id }}</VCardSubtitle>
        </VCol>
        <VCol
          class="text-right"
          cols="6"
        >
          <VBtn
            variant="tonal"
            color="primary"
            prepend-icon="tabler-arrow-back"
            @click="router.push({ name: PageMeta.Templates.List.Name })"
          >
            Back to List
          </VBtn>
        </VCol>
      </VRow>

      <!-- Page -->
      <VRow>
        <VCol cols="12">
          <TemplatePreviewPage
            v-if="route.params.guid"
            :template-g-u-i-d="route.params.guid"
          />
        </VCol>
      </VRow>
    </VCardItem>

    <!-- No Template State -->
    <VCardItem v-else>
      <VAlert
        type="warning"
        variant="tonal"
      >
        <VAlertTitle>No Template Found</VAlertTitle>
        <p>The requested template could not be found.</p>
      </VAlert>
    </VCardItem>
  </VCard>
</template>
